/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';

/*
 * This module produces WizardPage objects that maintain the state of a single page in a wizard.
 */
factory.$inject = ['$q', 'UtilService', 'WizardButtonFactory'];

/**
 * Defines the factory that supplies a generator used to create new WizardPage objects.
 *
 * @returns {function():WizardPage} a function that will generate a new WizardPage object when called.
 */
export function factory($q, UtilService, WizardButtonFactory) {
  // WizardPage is a function that produces a new object
  return {
    generate: WizardPage
  };

  /**
   * Returns a new WizardPage object.
   *
   * @returns {WizardPage}
   */
  function WizardPage() {
    /**
     * The text or a function returning the text to be displayed in the title portion of the wizard header.
     *
     * @type {string | function():string}
     */
    var headerText;

    /**
     * The parameters or a function returning the parameters to be passed from a page to the next state in the flow,
     * indexed by step number.
     *
     * @type {object | function():object}
     */
    var stateParams = {};

    /**
     * An optional array or a function returning an array of definitions of buttons to be displayed for custom actions
     * on a page, indexed by step number. These would typically show up on the last page.
     * See shared/wizard/wizardButton.js for a description of the button fields.
     *
     * @type {object[] | function():object[]}
     */
    var customButtons = [];

    /**
     * An optional boolean or a function returning a boolean to indicate whether the user has interacted with the
     * controls on a page, indexed by step number.  This is used for determining whether a confirmation should be
     * displayed when the user clicks away from the wizard. Defaults to true.
     *
     * @type {boolean | function():boolean}
     */
    var touched;

    /**
     * An optional boolean or a function returning a boolean to indicate whether the settings on a page are valid
     * (i.e. is Next allowed), indexed by step number. Defaults to true.
     *
     * @type {boolean | function():boolean}
     */
    var valid;

    /**
     * An optional function that will be called when the user tries to navigate away from a page using Prev or Next,
     * indexed by step number. The page can check for any unsaved changes. The function should return a promise that
     * resolves when it's ok to leave the page and rejects when it's not.
     *
     * @type {function():Promise}
     */
    var confirmLeaveFn;

    // Expose the functions only, not the data
    return {
      addCustomButton: addCustomButton,
      confirmLeave: confirmLeave,
      getCustomButtons: getCustomButtons,
      getHeaderText: getHeaderText,
      getTouched: getTouched,
      getValid: getValid,
      getStateParams: getStateParams,
      setConfirmLeave: setConfirmLeave,
      setCustomButtons: setCustomButtons,
      setHeaderText: setHeaderText,
      setTouched: setTouched,
      setValid: setValid,
      setStateParams: setStateParams
    };

    /**
     * Returns the array of definitions of buttons to be displayed for custom actions on this page.
     *
     * @returns {object[]} the array of definitions of buttons to be displayed for custom actions on this page.
     */
    function getCustomButtons() {
      return UtilService.getArray(customButtons);
    }

    /**
     * Sets the array or a function returning an array of definitions of buttons to be displayed for custom actions on
     * this page.
     *
     * @param {object[] | function():object[]} buttonsOrFn - The array or a function returning an array of
     *   definitions of buttons to be displayed for custom actions on this page.
     */
    function setCustomButtons(buttonsOrFn) {
      if (UtilService.isSet(buttonsOrFn)) {
        ValidationUtils.assertIsArrayOrFn(buttonsOrFn, 'customButtons');
        customButtons = buttonsOrFn;
      } else {
        customButtons = [];
      }
    }

    /**
     * Generates and adds a button to be displayed for a custom action on this page. The caller should customize the
     * returned WizardButton object.
     *
     * @returns {WizardButton} a new WizardButton object that has been added to the page and is ready for
     * customization.
     */
    function addCustomButton() {
      var button = WizardButtonFactory.generate();
      customButtons.push(button);
      return button;
    }

    /**
     * Returns the text to be displayed in the title portion of the wizard header on this page.
     *
     * @returns {string} the text to be displayed in the title portion of the wizard header on this page.
     */
    function getHeaderText() {
      return UtilService.getString(headerText);
    }

    /**
     * Sets the text to be displayed in the title portion of the wizard header on this page.
     *
     * @param {string | function():string} textOrFn - The text or a function returning the text to be displayed in the
     * title portion of the wizard header on this page.
     */
    function setHeaderText(textOrFn) {
      if (UtilService.isSet(textOrFn)) {
        ValidationUtils.assertIsStringOrFn(textOrFn, 'headerText');
        headerText = textOrFn;
      } else {
        headerText = undefined;
      }
    }

    /**
     * Returns the parameters to be passed from this page to the next state in the flow.
     *
     * @return {object} the parameters to be passed from this page to the next state in the flow.
     */
    function getStateParams() {
      return UtilService.getObject(stateParams);
    }

    /**
     * Sets the parameters or a function returning the parameters to be passed from this page to the next state in
     * the flow.
     *
     * @type {object | function():object} paramsOrFn - The parameters or a function returning the parameters to be
     *   passed from this page to the next state in the flow.
     */
    function setStateParams(paramsOrFn) {
      if (UtilService.isSet(paramsOrFn)) {
        ValidationUtils.assertIsObjectOrFn(paramsOrFn, 'stateParams');
        stateParams = paramsOrFn;
      } else {
        stateParams = {};
      }
    }

    /**
     * Determines whether the user has interacted with the controls on this page.
     *
     * @returns {boolean} true or false, indicating whether the user has interacted with the controls on this
     * page.
     */
    function getTouched() {
      var isTouched = UtilService.getBoolean(touched);

      // Defaults to touched
      return isTouched !== false;
    }

    /**
     * Sets the value that indicates whether the user has interacted with the controls on this page.
     *
     * @param {boolean | function():boolean} booleanOrFn - The value or a function returning the value that indicates
     * whether the user has interacted with the controls on this page.
     */
    function setTouched(booleanOrFn) {
      if (UtilService.isSet(booleanOrFn)) {
        ValidationUtils.assertIsBooleanOrFn(booleanOrFn, 'touched');
        touched = booleanOrFn;
      } else {
        touched = undefined;
      }
    }

    /**
     * Determines whether the settings on this page are valid.
     *
     * @returns {boolean} true or false, indicating whether the settings on this page are valid.
     */
    function getValid() {
      var isValid = UtilService.getBoolean(valid);

      // Defaults to valid
      return isValid !== false;
    }

    /**
     * Sets the value that indicates whether the settings on this page are valid.
     *
     * @param {boolean | function():boolean} booleanOrFn - The value or a function returning the value that indicates
     * whether the settings on this page are valid.
     */
    function setValid(booleanOrFn) {
      if (UtilService.isSet(booleanOrFn)) {
        ValidationUtils.assertIsBooleanOrFn(booleanOrFn, 'valid');
        valid = booleanOrFn;
      } else {
        valid = undefined;
      }
    }

    /**
     * Sets a function that will be called when the user tries to navigate away from this page using Prev or
     * Next. The function should return a promise that resolves when it's ok to leave the page and rejects when it's
     * not.
     *
     * @param {function([string]):Promise} fn - a function that will be called when the user tries to navigate away
     * from the current page using Prev or Next.
     */
    function setConfirmLeave(fn) {
      if (UtilService.isSet(fn)) {
        ValidationUtils.assertIsFunction(fn, 'confirmLeave');
        confirmLeaveFn = fn;
      } else {
        confirmLeaveFn = undefined;
      }
    }

    /**
     * Gives this page an opportunity to confirm any possible loss of unsaved changes.
     *
     * @param {String} direction - the WizardDirection the user is moving when they leave.
     * @returns {Promise} a promise that is resolved when there is nothing to confirm or when the user confirms. The
     * promise is rejected if the user decides to stay.
     */
    function confirmLeave(direction) {
      if (!confirmLeaveFn) {
        // Nothing to confirm
        return $q.when();
      }

      var result = confirmLeaveFn(direction);

      // Make sure the result looks like a promise
      if (!_.isObject(result) || !_.isFunction(result.then)) {
        throw new Error('Expected the confirmLeave function to return a promise but found ' + typeof result);
      }

      return result;
    }
  }
}
